import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { initSupabase } from "@veltro/shared/lib/supabase";
import { configureR2Storage } from "@veltro/shared/lib/r2Storage";
import { installUploadQueueListeners } from "@/shared/lib/uploadQueue";
import { SUPABASE_URL, SUPABASE_ANON_KEY, R2_PUBLIC_URL } from "@/config/env";
import { AppProviders } from "./providers";
import { App } from "./App";
import "@/config/i18n"; // initialize i18n before render
import "@/styles/index.css";

// Initialize Sentry error tracking (no-op if DSN not set)
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  });
}

// Initialize Supabase client before React renders
initSupabase(SUPABASE_URL, SUPABASE_ANON_KEY);
configureR2Storage({ publicUrl: R2_PUBLIC_URL });
installUploadQueueListeners();

// ── Stale chunk reload handler ────────────────────────────────────────────
// After a deploy, old JS chunk filenames no longer exist on the server.
// Detect the dynamic-import error and reload once to fetch the new manifest.
const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Failed to load module script|error loading dynamically imported module/i;
const CHUNK_RELOAD_FLAG = "veltro_chunk_reload";

function handleChunkError(message: string): void {
  if (!CHUNK_ERROR_PATTERN.test(message)) return;
  if (sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
    sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
    return;
  }
  sessionStorage.setItem(CHUNK_RELOAD_FLAG, "1");
  window.location.reload();
}

window.addEventListener("error", (event) => {
  handleChunkError(event.message ?? "");
});

window.addEventListener("unhandledrejection", (event) => {
  const message =
    event.reason instanceof Error
      ? event.reason.message
      : String(event.reason ?? "");
  handleChunkError(message);
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AppProviders>
      <App />
    </AppProviders>
  </StrictMode>
);
